/**
 * Error Tracking Service
 * Handles error reporting, performance monitoring, and user feedback in production
 */

import { productionConfig, isProduction } from '../config/production';

export interface ErrorReport {
  message: string;
  stack?: string;
  url: string;
  lineNumber?: number;
  columnNumber?: number;
  timestamp: string;
  userAgent: string;
  userId?: string;
  sessionId: string;
  buildVersion: string;
  environment: string;
  additionalData?: Record<string, any>;
}

export interface PerformanceReport {
  metric: string;
  value: number;
  timestamp: string;
  url: string;
  sessionId: string;
  additionalData?: Record<string, any>;
}

export interface UserFeedback {
  type: 'bug' | 'feature' | 'improvement' | 'other';
  message: string;
  email?: string;
  timestamp: string;
  url: string;
  sessionId: string;
  userAgent: string;
  additionalData?: Record<string, any>;
}

class ErrorTrackingService {
  private sessionId: string;
  private isInitialized = false;
  private errorQueue: ErrorReport[] = [];
  private performanceQueue: PerformanceReport[] = [];
  private feedbackQueue: UserFeedback[] = [];

  constructor() {
    this.sessionId = this.generateSessionId();
  }

  /**
   * Initialize error tracking service
   */
  public initialize(): void {
    if (this.isInitialized || !productionConfig.errorTracking.enabled) {
      return;
    }

    this.setupGlobalErrorHandlers();
    this.setupUnhandledRejectionHandler();
    this.setupPerformanceMonitoring();
    this.startPeriodicReporting();
    
    this.isInitialized = true;
    console.log('Error tracking service initialized');
  }

  /**
   * Report an error manually
   */
  public reportError(
    error: Error | string,
    additionalData?: Record<string, any>
  ): void {
    if (!productionConfig.errorTracking.enabled) {
      return;
    }

    const errorReport: ErrorReport = {
      message: typeof error === 'string' ? error : error.message,
      stack: typeof error === 'object' ? error.stack : undefined,
      url: window.location.href,
      timestamp: new Date().toISOString(),
      userAgent: navigator.userAgent,
      sessionId: this.sessionId,
      buildVersion: productionConfig.app.version,
      environment: productionConfig.app.environment,
      additionalData,
    };

    this.queueError(errorReport);
  }

  /**
   * Report performance metrics
   */
  public reportPerformance(
    metric: string,
    value: number,
    additionalData?: Record<string, any>
  ): void {
    if (!productionConfig.errorTracking.enablePerformanceTracking) {
      return;
    }

    const performanceReport: PerformanceReport = {
      metric,
      value,
      timestamp: new Date().toISOString(),
      url: window.location.href,
      sessionId: this.sessionId,
      additionalData,
    };

    this.queuePerformance(performanceReport);
  }

  /**
   * Submit user feedback
   */
  public submitFeedback(feedback: Omit<UserFeedback, 'timestamp' | 'url' | 'sessionId' | 'userAgent'>): void {
    if (!productionConfig.errorTracking.enableUserFeedback) {
      return;
    }

    const feedbackReport: UserFeedback = {
      ...feedback,
      timestamp: new Date().toISOString(),
      url: window.location.href,
      sessionId: this.sessionId,
      userAgent: navigator.userAgent,
    };

    this.queueFeedback(feedbackReport);
  }

  /**
   * Set user context for error reports
   */
  public setUserContext(userId: string): void {
    // Store user ID for future error reports
    sessionStorage.setItem('errorTracking_userId', userId);
  }

  /**
   * Clear user context
   */
  public clearUserContext(): void {
    sessionStorage.removeItem('errorTracking_userId');
  }

  /**
   * Flush all queued items immediately (useful for testing)
   */
  public flushQueues(): Promise<void> {
    const promises: Promise<void>[] = [];

    if (this.errorQueue.length > 0) {
      const errors = [...this.errorQueue];
      this.errorQueue = [];
      promises.push(this.sendErrors(errors));
    }

    if (this.performanceQueue.length > 0) {
      const metrics = [...this.performanceQueue];
      this.performanceQueue = [];
      promises.push(this.sendPerformanceMetrics(metrics));
    }

    if (this.feedbackQueue.length > 0) {
      const feedback = [...this.feedbackQueue];
      this.feedbackQueue = [];
      promises.push(this.sendFeedback(feedback));
    }

    return Promise.all(promises).then(() => undefined);
  }

  /**
   * Setup global error handlers
   */
  private setupGlobalErrorHandlers(): void {
    // Handle JavaScript errors
    window.addEventListener('error', (event) => {
      const errorReport: ErrorReport = {
        message: event.message,
        stack: event.error?.stack,
        url: window.location.href,
        lineNumber: event.lineno,
        columnNumber: event.colno,
        timestamp: new Date().toISOString(),
        userAgent: navigator.userAgent,
        userId: sessionStorage.getItem('errorTracking_userId') || undefined,
        sessionId: this.sessionId,
        buildVersion: productionConfig.app.version,
        environment: productionConfig.app.environment,
      };

      this.queueError(errorReport);
    });

    // Handle resource loading errors
    window.addEventListener('error', (event) => {
      if (event.target !== window) {
        const target = event.target as HTMLElement;
        const errorReport: ErrorReport = {
          message: `Resource loading error: ${target.tagName}`,
          url: window.location.href,
          timestamp: new Date().toISOString(),
          userAgent: navigator.userAgent,
          userId: sessionStorage.getItem('errorTracking_userId') || undefined,
          sessionId: this.sessionId,
          buildVersion: productionConfig.app.version,
          environment: productionConfig.app.environment,
          additionalData: {
            resourceType: target.tagName,
            resourceSrc: (target as any).src || (target as any).href,
          },
        };

        this.queueError(errorReport);
      }
    }, true);
  }

  /**
   * Setup unhandled promise rejection handler
   */
  private setupUnhandledRejectionHandler(): void {
    window.addEventListener('unhandledrejection', (event) => {
      const errorReport: ErrorReport = {
        message: `Unhandled Promise Rejection: ${event.reason}`,
        stack: event.reason?.stack,
        url: window.location.href,
        timestamp: new Date().toISOString(),
        userAgent: navigator.userAgent,
        userId: sessionStorage.getItem('errorTracking_userId') || undefined,
        sessionId: this.sessionId,
        buildVersion: productionConfig.app.version,
        environment: productionConfig.app.environment,
        additionalData: {
          promiseRejection: true,
          reason: event.reason,
        },
      };

      this.queueError(errorReport);
    });
  }

  /**
   * Setup performance monitoring
   */
  private setupPerformanceMonitoring(): void {
    if (!productionConfig.errorTracking.enablePerformanceTracking) {
      return;
    }

    // Monitor Core Web Vitals
    try {
      import('web-vitals').then((webVitals: any) => {
        // Use dynamic property access to avoid TypeScript issues
        const vitalsModule = webVitals;
        
        if (vitalsModule.onCLS) {
          vitalsModule.onCLS((metric: any) => this.reportPerformance('CLS', metric.value));
        }
        if (vitalsModule.onINP) {
          vitalsModule.onINP((metric: any) => this.reportPerformance('INP', metric.value));
        }
        if (vitalsModule.onFCP) {
          vitalsModule.onFCP((metric: any) => this.reportPerformance('FCP', metric.value));
        }
        if (vitalsModule.onLCP) {
          vitalsModule.onLCP((metric: any) => this.reportPerformance('LCP', metric.value));
        }
        if (vitalsModule.onTTFB) {
          vitalsModule.onTTFB((metric: any) => this.reportPerformance('TTFB', metric.value));
        }
      }).catch(() => {
        console.warn('Failed to load web-vitals library');
      });
    } catch (error) {
      console.warn('Web vitals not available');
    }

    // Monitor navigation timing
    if ('performance' in window && 'getEntriesByType' in performance) {
      window.addEventListener('load', () => {
        setTimeout(() => {
          const navigation = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming;
          if (navigation) {
            this.reportPerformance('domContentLoaded', navigation.domContentLoadedEventEnd - navigation.domContentLoadedEventStart);
            this.reportPerformance('loadComplete', navigation.loadEventEnd - navigation.loadEventStart);
            this.reportPerformance('totalLoadTime', navigation.loadEventEnd - navigation.fetchStart);
          }
        }, 0);
      });
    }
  }

  /**
   * Queue error for batch reporting
   */
  private queueError(error: ErrorReport): void {
    // Apply sampling rate
    if (Math.random() > productionConfig.errorTracking.sampleRate) {
      return;
    }

    this.errorQueue.push(error);

    // Log to console in development
    if (!isProduction()) {
      console.error('Error tracked:', error);
    }

    // Send immediately for critical errors
    if (this.isCriticalError(error)) {
      this.sendErrors([error]);
      this.errorQueue = this.errorQueue.filter(e => e !== error);
    }
  }

  /**
   * Queue performance metric for batch reporting
   */
  private queuePerformance(performance: PerformanceReport): void {
    this.performanceQueue.push(performance);

    // Log to console in development
    if (!isProduction()) {
      console.log('Performance tracked:', performance);
    }
  }

  /**
   * Queue feedback for batch reporting
   */
  private queueFeedback(feedback: UserFeedback): void {
    this.feedbackQueue.push(feedback);

    // Log to console in development
    if (!isProduction()) {
      console.log('Feedback tracked:', feedback);
    }
  }

  /**
   * Check if error is critical and should be sent immediately
   */
  private isCriticalError(error: ErrorReport): boolean {
    const criticalPatterns = [
      /security/i,
      /authentication/i,
      /payment/i,
      /data loss/i,
      /corruption/i,
    ];

    return criticalPatterns.some(pattern => pattern.test(error.message));
  }

  /**
   * Send errors to tracking service
   */
  private async sendErrors(errors: ErrorReport[]): Promise<void> {
    if (errors.length === 0) {
      return;
    }

    try {
      // In a real implementation, this would send to your error tracking service
      // For now, we'll use a mock endpoint or local storage
      if (isProduction()) {
        await fetch('/api/errors', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ errors }),
        });
      } else {
        // Store in local storage for development
        const existingErrors = JSON.parse(localStorage.getItem('errorTracking_errors') || '[]');
        localStorage.setItem('errorTracking_errors', JSON.stringify([...existingErrors, ...errors]));
      }
    } catch (error) {
      console.warn('Failed to send error reports:', error);
    }
  }

  /**
   * Send performance metrics to tracking service
   */
  private async sendPerformanceMetrics(metrics: PerformanceReport[]): Promise<void> {
    if (metrics.length === 0) {
      return;
    }

    try {
      if (isProduction()) {
        await fetch('/api/performance', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ metrics }),
        });
      } else {
        // Store in local storage for development
        const existingMetrics = JSON.parse(localStorage.getItem('errorTracking_performance') || '[]');
        localStorage.setItem('errorTracking_performance', JSON.stringify([...existingMetrics, ...metrics]));
      }
    } catch (error) {
      console.warn('Failed to send performance metrics:', error);
    }
  }

  /**
   * Send feedback to tracking service
   */
  private async sendFeedback(feedback: UserFeedback[]): Promise<void> {
    if (feedback.length === 0) {
      return;
    }

    try {
      if (isProduction()) {
        await fetch('/api/feedback', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ feedback }),
        });
      } else {
        // Store in local storage for development
        const existingFeedback = JSON.parse(localStorage.getItem('errorTracking_feedback') || '[]');
        localStorage.setItem('errorTracking_feedback', JSON.stringify([...existingFeedback, ...feedback]));
      }
    } catch (error) {
      console.warn('Failed to send feedback:', error);
    }
  }

  /**
   * Start periodic reporting of queued items
   */
  private startPeriodicReporting(): void {
    // Send queued items every 30 seconds
    setInterval(() => {
      if (this.errorQueue.length > 0) {
        const errors = [...this.errorQueue];
        this.errorQueue = [];
        this.sendErrors(errors);
      }

      if (this.performanceQueue.length > 0) {
        const metrics = [...this.performanceQueue];
        this.performanceQueue = [];
        this.sendPerformanceMetrics(metrics);
      }

      if (this.feedbackQueue.length > 0) {
        const feedback = [...this.feedbackQueue];
        this.feedbackQueue = [];
        this.sendFeedback(feedback);
      }
    }, 30000);

    // Send queued items before page unload
    window.addEventListener('beforeunload', () => {
      if (this.errorQueue.length > 0) {
        navigator.sendBeacon('/api/errors', JSON.stringify({ errors: this.errorQueue }));
      }
      if (this.performanceQueue.length > 0) {
        navigator.sendBeacon('/api/performance', JSON.stringify({ metrics: this.performanceQueue }));
      }
      if (this.feedbackQueue.length > 0) {
        navigator.sendBeacon('/api/feedback', JSON.stringify({ feedback: this.feedbackQueue }));
      }
    });
  }

  /**
   * Generate unique session ID
   */
  private generateSessionId(): string {
    return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  }
}

// Export singleton instance
export const errorTrackingService = new ErrorTrackingService();